import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import Navbar from '../components/Navbar';
import ProfileModal from '../components/ProfileModal';
import AddUserModal from '../components/AddUserModal';
import EditUserModal from '../components/EditUserModal';
import ResetPasswordModal from '../components/ResetPasswordModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import QuickWebsiteModal from '../components/QuickWebsiteModal';
import {
  FaUsers,
  FaUserCheck,
  FaUserSlash,
  FaBuilding,
  FaUserPlus,
  FaSearch,
  FaSync,
  FaEdit,
  FaKey,
  FaTrash,
  FaGlobe,
  FaToggleOn,
  FaToggleOff,
  FaMapMarkerAlt,
  FaChevronRight,
  FaShieldAlt,
  FaEye,
} from 'react-icons/fa';

export default function DashboardPage() {
  const { user } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [usertypeFilter, setUsertypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Modals state
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  const [selectedUserForDelete, setSelectedUserForDelete] = useState(null);
  const [selectedUserForWebsites, setSelectedUserForWebsites] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (usertypeFilter !== '') params.usertype = usertypeFilter;
      if (statusFilter !== '') params.status = statusFilter;

      const res = await api.get('/users', { params });
      if (res.data.success) {
        setUsers(res.data.users || []);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  }, [search, usertypeFilter, statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleToggleStatus = async (targetUser) => {
    try {
      const res = await api.patch(`/users/${targetUser.userid}/toggle-status`);
      if (res.data.success) {
        setUsers((prev) =>
          prev.map((u) =>
            u.userid === targetUser.userid ? { ...u, is_active: res.data.is_active } : u
          )
        );
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to change status');
    }
  };

  const handleDeleteUser = async (targetUser) => {
    setDeleting(true);
    try {
      const res = await api.delete(`/users/${targetUser.userid}`);
      if (res.data.success) {
        setSelectedUserForDelete(null);
        fetchUsers();
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  // Stats calculation
  const totalUsers = users.length;
  const activeCount = users.filter((u) => u.is_active).length;
  const inactiveCount = users.filter((u) => !u.is_active).length;
  const stationCount = users.filter((u) => u.usertype === 1).length;
  const liuCount = users.filter((u) => u.usertype === 51).length;
  const dcrbCount = users.filter((u) => u.usertype === 52).length;

  // Only Super Admin (0) and District Admin (5) have rights to manage and register children
  const canAddUsers = [0, 5].includes(user?.usertype);
  const canManageUsers = [0, 5].includes(user?.usertype);
  const isViewOnlySupervisor = [7, 6].includes(user?.usertype);

  return (
    <div className="app-container">
      <Navbar onOpenProfile={() => setIsProfileOpen(true)} />

      <main className="dashboard-content">
        {/* Identity Banner */}
        <div className="jurisdiction-banner">
          <div className="jurisdiction-info">
            <div className="jurisdiction-icon">
              <FaShieldAlt />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <h2 style={{ fontSize: '1.4rem' }}>{user?.name || user?.username}</h2>
                <span className={`badge badge-${user?.usertype}`}>{user?.role_label}</span>
                {isViewOnlySupervisor && (
                  <span
                    className="badge badge-warning"
                    style={{
                      background: 'rgba(245, 158, 11, 0.15)',
                      color: '#fbbf24',
                      border: '1px solid rgba(245, 158, 11, 0.3)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      fontSize: '0.75rem',
                      padding: '4px 8px',
                    }}
                    title="Range & Zone accounts have view-only access to subordinate accounts"
                  >
                    <FaEye /> View Only
                  </span>
                )}
              </div>
              <div className="hierarchy-crumbs">
                <span>Account ID: {user?.username}</span>
                {user?.jurisdiction?.details?.zone_name && (
                  <>
                    <FaChevronRight style={{ fontSize: '0.65rem' }} />
                    <span>Zone: <strong className="crumb-item">{user.jurisdiction.details.zone_name}</strong></span>
                  </>
                )}
                {user?.jurisdiction?.details?.range_name && (
                  <>
                    <FaChevronRight style={{ fontSize: '0.65rem' }} />
                    <span>Range: <strong className="crumb-item">{user.jurisdiction.details.range_name}</strong></span>
                  </>
                )}
                {user?.jurisdiction?.details?.district_name && (
                  <>
                    <FaChevronRight style={{ fontSize: '0.65rem' }} />
                    <span>District: <strong className="crumb-item">{user.jurisdiction.details.district_name}</strong></span>
                  </>
                )}
                {user?.jurisdiction?.location_name && (
                  <>
                    <FaChevronRight style={{ fontSize: '0.65rem' }} />
                    <span style={{ color: 'var(--text-main)', fontWeight: 600 }}>
                      Jurisdiction: {user.jurisdiction.location_name}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          <button
            className="btn btn-secondary"
            onClick={() => setIsProfileOpen(true)}
            style={{ padding: '8px 16px', fontSize: '0.85rem' }}
          >
            <FaEdit />
            <span>Change My Name / Profile</span>
          </button>
        </div>

        {/* Statistics Grid */}
        <div className="stats-grid">
          <div className="stat-card">
            <div>
              <div className="stat-val">{totalUsers}</div>
              <div className="stat-title">
                {canAddUsers
                  ? 'Sub-Units Managed'
                  : isViewOnlySupervisor
                  ? 'Sub-Units (View Only)'
                  : 'My Account Record'}
              </div>
            </div>
            <div className="stat-icon" style={{ background: 'rgba(99, 102, 241, 0.15)', color: 'var(--primary)' }}>
              <FaUsers />
            </div>
          </div>

          <div className="stat-card">
            <div>
              <div className="stat-val" style={{ color: '#34d399' }}>{activeCount}</div>
              <div className="stat-title">Active Accounts</div>
            </div>
            <div className="stat-icon" style={{ background: 'rgba(16, 185, 129, 0.15)', color: 'var(--success)' }}>
              <FaUserCheck />
            </div>
          </div>

          <div className="stat-card">
            <div>
              <div className="stat-val" style={{ color: '#f87171' }}>{inactiveCount}</div>
              <div className="stat-title">Inactive Accounts</div>
            </div>
            <div className="stat-icon" style={{ background: 'rgba(239, 68, 68, 0.15)', color: 'var(--danger)' }}>
              <FaUserSlash />
            </div>
          </div>

          <div className="stat-card">
            <div>
              <div className="stat-val" style={{ color: '#38bdf8' }}>
                {stationCount} <span style={{ fontSize: '0.9rem', color: 'var(--text-dim)' }}>(+{liuCount + dcrbCount} LIU/DCRB)</span>
              </div>
              <div className="stat-title">Police Stations & Special Units</div>
            </div>
            <div className="stat-icon" style={{ background: 'rgba(14, 165, 233, 0.15)', color: 'var(--secondary)' }}>
              <FaBuilding />
            </div>
          </div>
        </div>

        {/* Main User Management Panel */}
        <div className="glass-panel">
          <div className="table-controls">
            <div className="search-filter-box">
              <div className="search-input-wrapper">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by Name, Username, Mobile..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {[0, 7, 6, 5].includes(user?.usertype) && (
                <select
                  className="form-control"
                  style={{ width: 'auto', minWidth: '180px' }}
                  value={usertypeFilter}
                  onChange={(e) => setUsertypeFilter(e.target.value)}
                >
                  <option value="">All Roles / Types</option>
                  {user?.usertype === 0 && (
                    <>
                      <option value="0">Super Admin (0)</option>
                      <option value="7">Zone Level (7)</option>
                    </>
                  )}
                  {(user?.usertype === 0 || user?.usertype === 7) && (
                    <option value="6">Range Level (6)</option>
                  )}
                  {(user?.usertype === 0 || user?.usertype === 7 || user?.usertype === 6) && (
                    <option value="5">District Level (5)</option>
                  )}
                  <option value="51">LIU District (51)</option>
                  <option value="52">DCRB District (52)</option>
                  <option value="1">Police Station (1)</option>
                </select>
              )}

              <select
                className="form-control"
                style={{ width: 'auto', minWidth: '140px' }}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>

              <button
                className="btn btn-secondary"
                onClick={fetchUsers}
                title="Refresh Table Data"
                style={{ padding: '10px 14px' }}
              >
                <FaSync className={loading ? 'fa-spin' : ''} />
              </button>
            </div>

            {canAddUsers && (
              <button
                className="btn btn-primary"
                onClick={() => setIsAddUserOpen(true)}
              >
                <FaUserPlus />
                <span>Register User</span>
              </button>
            )}
          </div>

          <div className="table-responsive">
            <table className="custom-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>User / Office</th>
                  <th>Role</th>
                  <th>Assigned Jurisdiction</th>
                  <th>Authorized Websites</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                      Loading user accounts...
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                      No matching user accounts found.
                    </td>
                  </tr>
                ) : (
                  users.map((u, idx) => {
                    const isSelf = u.userid === user?.userid;
                    return (
                      <tr key={u.userid}>
                        <td style={{ color: 'var(--text-dim)', fontWeight: 600, width: '50px' }}>
                          {idx + 1}
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: 600, color: 'var(--text-main)', fontSize: '0.92rem' }}>
                              {u.name || 'Unnamed Office'}
                            </span>
                            <span style={{ fontSize: '0.78rem', color: 'var(--secondary)', fontFamily: 'monospace' }}>
                              @{u.username}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className={`badge badge-${u.usertype}`}>{u.role_label}</span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }}>
                            <FaMapMarkerAlt style={{ color: 'var(--text-dim)', fontSize: '0.8rem' }} />
                            <span>{u.location_name || 'State / Global'}</span>
                          </div>
                          {u.district_name && u.access_type === 'STATION' && (
                            <span style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginLeft: '18px' }}>
                              District: {u.district_name}
                            </span>
                          )}
                        </td>
                        <td>
                          {/* Website Permissions Badges — clickable only for admin */}
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '4px',
                              maxWidth: '240px',
                              cursor: user?.usertype === 0 ? 'pointer' : 'default',
                            }}
                            onClick={() => user?.usertype === 0 && setSelectedUserForWebsites(u)}
                            title={user?.usertype === 0 ? 'Click to manage website access' : 'Authorized websites'}
                          >
                            {u.websites && u.websites.length > 0 ? (
                              u.websites.map((site) => (
                                <span
                                  key={site.id}
                                  style={{
                                    fontSize: '0.68rem',
                                    padding: '2px 7px',
                                    borderRadius: '4px',
                                    background: 'rgba(99, 102, 241, 0.15)',
                                    color: 'var(--info)',
                                    border: '1px solid rgba(99, 102, 241, 0.3)',
                                    fontWeight: 600,
                                  }}
                                >
                                  {site.name}
                                </span>
                              ))
                            ) : (
                              <span style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>
                                No websites assigned
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${u.is_active ? 'badge-active' : 'badge-inactive'}`}>
                            {u.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <div className="actions-cell">
                            {isSelf ? (
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setIsProfileOpen(true)}
                                title="Change your own name and account details"
                              >
                                <FaEdit />
                                <span>Edit My Profile</span>
                              </button>
                            ) : canManageUsers ? (
                              <>
                                {/* Globe website button — admin only */}
                                {user?.usertype === 0 && (
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => setSelectedUserForWebsites(u)}
                                    title="Manage Website Access (Admin Only)"
                                  >
                                    <FaGlobe />
                                  </button>
                                )}
                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => setSelectedUserForEdit(u)}
                                  title="Edit User Details"
                                >
                                  <FaEdit />
                                  <span>Edit</span>
                                </button>
                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => setSelectedUserForPassword(u)}
                                  title="Reset User Password"
                                >
                                  <FaKey />
                                </button>
                                <button
                                  className={`btn btn-sm ${u.is_active ? 'btn-danger' : 'btn-success'}`}
                                  onClick={() => handleToggleStatus(u)}
                                  title={u.is_active ? 'Deactivate user' : 'Activate user'}
                                >
                                  {u.is_active ? <FaToggleOn /> : <FaToggleOff />}
                                </button>
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => setSelectedUserForDelete(u)}
                                  title="Delete User Permanently"
                                >
                                  <FaTrash />
                                </button>
                              </>
                            ) : (
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setSelectedUserForEdit(u)}
                                title="View User Details (Read Only)"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  padding: '5px 12px',
                                  fontSize: '0.8rem',
                                  borderColor: 'rgba(99, 102, 241, 0.3)',
                                  background: 'rgba(99, 102, 241, 0.08)',
                                  color: 'var(--text-main)',
                                }}
                              >
                                <FaEye style={{ color: 'var(--primary-light, #818cf8)' }} />
                                <span>View Details</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Modals */}
      <ProfileModal
        isOpen={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
      />

      <AddUserModal
        isOpen={isAddUserOpen}
        onClose={() => setIsAddUserOpen(false)}
        onUserAdded={fetchUsers}
      />

      <EditUserModal
        isOpen={Boolean(selectedUserForEdit)}
        user={selectedUserForEdit}
        onClose={() => setSelectedUserForEdit(null)}
        onUserUpdated={fetchUsers}
      />

      <ResetPasswordModal
        isOpen={Boolean(selectedUserForPassword)}
        user={selectedUserForPassword}
        onClose={() => setSelectedUserForPassword(null)}
      />

      <QuickWebsiteModal
        isOpen={Boolean(selectedUserForWebsites)}
        user={selectedUserForWebsites}
        onClose={() => setSelectedUserForWebsites(null)}
        onUpdated={fetchUsers}
      />

      <DeleteConfirmationModal
        isOpen={Boolean(selectedUserForDelete)}
        user={selectedUserForDelete}
        onClose={() => setSelectedUserForDelete(null)}
        onConfirm={handleDeleteUser}
        deleting={deleting}
      />
    </div>
  );
}
